import React, { useEffect, useState } from 'react';
import { CreditCard, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { Button, Card, Input, Modal } from '../../../components/ui';
import { creditoService } from '../../../services/creditoService';
import type { Cliente, CreditoMovimientoGlobal } from '../../../types/clientes.types';
import { formatBackendDateTimeCR } from '../../../utils/date';

const colones = (v: number | string | null | undefined) =>
  `₡${Math.round(Number(v || 0)).toLocaleString('es-CR')}`;

const TIPO_LABEL: Record<string, string> = {
  CREDITO_OTORGADO: 'Crédito otorgado',
  CARGO_VENTA: 'Venta a crédito',
  ABONO_RECIBIDO: 'Abono recibido',
  AJUSTE_POSITIVO: 'Ajuste +',
  AJUSTE_NEGATIVO: 'Ajuste -',
  PAGO_PREMIO_EFECTIVO: 'Pago al cliente',
};

export const LocalCreditosPage: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [permiteAbonos, setPermiteAbonos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Abono modal
  const [clienteAbono, setClienteAbono] = useState<Cliente | null>(null);
  const [montoAbono, setMontoAbono] = useState('');
  const [descAbono, setDescAbono] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clientePago, setClientePago] = useState<Cliente | null>(null);
  const [montoPago, setMontoPago] = useState('');
  const [descPago, setDescPago] = useState('');

  // Historial expandible por cliente
  const [expandido, setExpandido] = useState<string | null>(null);
  const [movimientos, setMovimientos] = useState<CreditoMovimientoGlobal[]>([]);
  const [loadingMov, setLoadingMov] = useState(false);

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const [clientesData, resumen] = await Promise.all([
        creditoService.listarClientesLocal(),
        creditoService.obtenerResumenLocal(),
      ]);
      setClientes(clientesData);
      setPermiteAbonos(Boolean((resumen as any)?.config_local?.puede_registrar_abonos));
    } catch (err: any) {
      setError(err.message || 'No se pudo cargar créditos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const toggleHistorial = async (clienteId: string) => {
    if (expandido === clienteId) {
      setExpandido(null);
      return;
    }
    setExpandido(clienteId);
    setLoadingMov(true);
    try {
      const data = await creditoService.listarMovimientosLocal({ cliente_id: clienteId });
      setMovimientos(data);
    } catch {
      setMovimientos([]);
    } finally {
      setLoadingMov(false);
    }
  };

  const confirmarAbono = async () => {
    if (!clienteAbono) return;
    const monto = Number(montoAbono);
    if (!Number.isFinite(monto) || monto <= 0) {
      setError('Monto inválido');
      return;
    }
    setSubmitting(true);
    try {
      await creditoService.abonarClienteLocal(clienteAbono.id, { monto, descripcion: descAbono || 'Abono registrado' });
      setClienteAbono(null);
      setMontoAbono('');
      setDescAbono('');
      // Si el historial de ese cliente está abierto, recargarlo
      if (expandido === clienteAbono.id) {
        const data = await creditoService.listarMovimientosLocal({ cliente_id: clienteAbono.id });
        setMovimientos(data);
      }
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo registrar el abono');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmarPagoPremio = async () => {
    if (!clientePago) return;
    const monto = Number(montoPago);
    const saldoFavor = Math.abs(Number(clientePago.saldo_pendiente || 0));
    if (!Number.isFinite(monto) || monto <= 0 || monto > saldoFavor) {
      setError('Monto inválido para el saldo a favor');
      return;
    }
    setSubmitting(true);
    try {
      await creditoService.pagarPremioCliente(clientePago.id, { monto, descripcion: descPago || 'Pago de premio en efectivo' });
      setClientePago(null);
      setMontoPago('');
      setDescPago('');
      if (expandido === clientePago.id) {
        const data = await creditoService.listarMovimientosLocal({ cliente_id: clientePago.id });
        setMovimientos(data);
      }
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo registrar el pago al cliente');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPendiente = clientes.reduce((s, c) => s + Number(c.saldo_pendiente || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Créditos</h1>
          <p className="text-gray-500 text-sm">Clientes con saldo pendiente en este local.</p>
        </div>
        <Button variant="secondary" leftIcon={<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />} onClick={cargar}>
          Refrescar
        </Button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-2 gap-3">
        <Card><p className="text-xs text-gray-500">Clientes</p><p className="text-xl font-bold">{clientes.length}</p></Card>
        <Card><p className="text-xs text-gray-500">Balance clientes</p><p className={`text-xl font-bold ${totalPendiente < 0 ? 'text-green-600' : 'text-amber-600'}`}>{colones(totalPendiente)}</p></Card>
      </div>

      {!permiteAbonos && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Este local no tiene permiso para registrar abonos. Contacte a BANCA para habilitarlo.
        </div>
      )}

      <Card>
        <div className="flex items-center gap-2 mb-3">
          <CreditCard size={18} />
          <h2 className="font-semibold">Clientes de crédito</h2>
        </div>

        {clientes.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No hay clientes de crédito en este local.</p>
        ) : (
          <div className="space-y-1">
            {clientes.map((cliente) => {
              const saldo = Number(cliente.saldo_pendiente || 0);
              const abierto = expandido === cliente.id;
              return (
                <div key={cliente.id} className="border rounded-lg overflow-hidden">
                  {/* Fila principal */}
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    {/* Botón expandir historial */}
                    <button
                      className="text-gray-400 hover:text-gray-600"
                      onClick={() => toggleHistorial(cliente.id)}
                      title="Ver historial"
                    >
                      {abierto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>

                    {/* Nombre + teléfono */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{cliente.nombre}</p>
                      {cliente.telefono && <p className="text-xs text-gray-400">{cliente.telefono}</p>}
                    </div>

                    {/* Límite */}
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-400">Límite</p>
                      <p className="text-sm">{colones(cliente.limite_credito)}</p>
                    </div>

                    {/* Saldo */}
                    <div className="text-right w-28">
                      <p className="text-xs text-gray-400">{saldo < 0 ? 'A favor' : 'Saldo'}</p>
                      <p className={`font-bold ${saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>{colones(Math.abs(saldo))}</p>
                    </div>

                    {/* Acción */}
                    {permiteAbonos && saldo > 0 && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => { setClienteAbono(cliente); setMontoAbono(''); setDescAbono(''); }}
                      >
                        Abonar
                      </Button>
                    )}
                    {permiteAbonos && saldo < 0 && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => { setClientePago(cliente); setMontoPago(''); setDescPago(''); }}
                      >
                        Pagar al cliente
                      </Button>
                    )}
                  </div>

                  {/* Historial expandido */}
                  {abierto && (
                    <div className="border-t bg-gray-50 px-4 py-3">
                      {loadingMov ? (
                        <p className="text-sm text-gray-400">Cargando historial…</p>
                      ) : movimientos.length === 0 ? (
                        <p className="text-sm text-gray-400">Sin movimientos.</p>
                      ) : (
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="py-1">Fecha</th>
                              <th>Tipo</th>
                              <th className="text-right">Monto</th>
                              <th className="text-right">Saldo anterior</th>
                              <th className="text-right">Saldo nuevo</th>
                              <th>Descripción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {movimientos.map((mov) => (
                              <tr key={mov.id} className="border-b last:border-0">
                                <td className="py-1 whitespace-nowrap">
                                  {formatBackendDateTimeCR(mov.fecha_hora, { dateStyle: 'short', timeStyle: 'short' })}
                                </td>
                                <td className="whitespace-nowrap">
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                    mov.tipo_movimiento?.includes('ABONO') ? 'bg-green-100 text-green-700' :
                                    mov.tipo_movimiento?.includes('CREDITO') || mov.tipo_movimiento?.includes('CARGO') ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {TIPO_LABEL[mov.tipo_movimiento || ''] || mov.tipo_movimiento}
                                  </span>
                                </td>
                                <td className="text-right font-semibold">{colones(mov.monto)}</td>
                                <td className="text-right text-gray-500">{colones(mov.saldo_anterior)}</td>
                                <td className="text-right text-gray-500">{colones(mov.saldo_nuevo)}</td>
                                <td className="max-w-xs truncate text-gray-500">{mov.motivo || mov.descripcion || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Modal abono */}
      <Modal
        isOpen={!!clienteAbono}
        onClose={() => setClienteAbono(null)}
        title="Registrar abono"
        size="sm"
      >
        <div className="space-y-3">
          <p className="font-medium">{clienteAbono?.nombre}</p>
          <p className="text-sm text-gray-500">Saldo actual: <span className="font-bold text-red-600">{colones(clienteAbono?.saldo_pendiente)}</span></p>
          <Input
            label="Monto del abono"
            type="number"
            min={1}
            value={montoAbono}
            onChange={(e) => setMontoAbono(e.target.value)}
            placeholder="0"
          />
          <Input
            label="Descripción (opcional)"
            value={descAbono}
            onChange={(e) => setDescAbono(e.target.value)}
            placeholder="Abono en efectivo…"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button variant="secondary" onClick={() => setClienteAbono(null)} className="flex-1">Cancelar</Button>
            <Button variant="primary" onClick={confirmarAbono} disabled={submitting} className="flex-1">
              {submitting ? 'Guardando…' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!clientePago}
        onClose={() => setClientePago(null)}
        title="Pagar al cliente"
        size="sm"
      >
        <div className="space-y-3">
          <p className="font-medium">{clientePago?.nombre}</p>
          <p className="text-sm text-gray-500">Saldo a favor: <span className="font-bold text-green-600">{colones(Math.abs(Number(clientePago?.saldo_pendiente || 0)))}</span></p>
          <Input
            label="Monto a pagar"
            type="number"
            min={1}
            max={Math.abs(Number(clientePago?.saldo_pendiente || 0))}
            value={montoPago}
            onChange={(e) => setMontoPago(e.target.value)}
            placeholder="0"
          />
          <Input
            label="Descripción (opcional)"
            value={descPago}
            onChange={(e) => setDescPago(e.target.value)}
            placeholder="Pago de premio en efectivo"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button variant="secondary" onClick={() => setClientePago(null)} className="flex-1">Cancelar</Button>
            <Button variant="primary" onClick={confirmarPagoPremio} disabled={submitting} className="flex-1">
              {submitting ? 'Guardando…' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
